"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth-client";
import { photoSignInDestination } from "@/lib/photo-recovery";

export default function SignInPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(undefined);
    setIsSubmitting(true);

    let signInError;
    try {
      const result = await authClient.signIn.email({
        email: email.trim().toLowerCase(),
        password,
      });
      signInError = result.error;
    } catch {
      setError("Could not connect. Please try again.");
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(false);

    if (signInError) {
      if (process.env.NODE_ENV === "development") {
        const code = String(signInError.code ?? "UNKNOWN")
          .replace(/[^A-Z0-9_]/gi, "")
          .slice(0, 80);
        setError(
          `Sign-in request failed: HTTP ${signInError.status ?? "unknown"}, ${code}.`
        );
        return;
      }
      setError(
        "Couldn't sign in. Check your email and password, or ask your administrator to reset it."
      );
      return;
    }

    const destination = photoSignInDestination(window.location.search);
    if (destination !== "/") {
      // Full navigation avoids mounting any normal upload layout en route.
      window.location.assign(destination);
      return;
    }
    router.push(destination);
    router.refresh();
  };

  return (
    <div className="flex min-h-dvh items-center justify-center px-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-2xl">Oakwood Field App</CardTitle>
          <CardDescription>Sign in with your work account</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                autoComplete="username"
                id="email"
                onChange={(event) => setEmail(event.target.value)}
                required
                type="email"
                value={email}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  autoComplete="current-password"
                  id="password"
                  className="h-12 pr-20"
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  type={showPassword ? "text" : "password"}
                  value={password}
                />
                <button
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-controls="password"
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 min-w-16 rounded-r-md px-3 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2"
                  onClick={() => setShowPassword((visible) => !visible)}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            {error && (
              <p className="text-destructive text-base" role="alert">
                {error}
              </p>
            )}
            <Button className="mt-2" disabled={isSubmitting} type="submit">
              {isSubmitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <p className="text-muted-foreground mt-5 text-sm">
            Need an account or a password reset? Contact your Oakwood
            administrator.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
